import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import MarkdownEditor from './MarkdownEditor';
import { SmartTemplateField } from '../types';
import {
    FieldAssignment,
    composeStructuredFieldMarkdown as composeTemplateMarkdown,
    normalizeAssignments,
    parseStructuredFieldMarkdown as parseTemplateMarkdown,
} from '../utils/smartFieldMarkers';
import {
    ActiveResizeState,
    ActiveMoveState,
    LineMetric,
    adjustAssignmentsForContentChange,
    getVisualLineDescriptor,
    getFieldTint,
    getAssignmentPreview,
    isStructuralBlankLine,
    isSameAssignment,
    isSameResizeState,
    isSameMoveState,
    resizeAssignmentRange,
    moveAssignmentRange,
} from '../utils/smartFieldOverlayUtils';

interface TemplateContentEditorProps {
    value: string;
    onChange: (value: string) => void;
    smartFields: SmartTemplateField[];
    onSmartFieldsChange?: (fields: SmartTemplateField[]) => void;
    placeholder?: string;
    minHeight?: string;
    showSmartOverlay?: boolean;
}

const slugifyFieldCode = (label: string) => {
    const normalized = label
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9._-]+/g, '-')
        .replace(/-{2,}/g, '-')
        .replace(/^-+|-+$/g, '');

    return normalized || 'field';
};

const createUniqueFieldCode = (label: string, fields: SmartTemplateField[]) => {
    const baseCode = slugifyFieldCode(label);
    const takenCodes = new Set(fields.map((field) => field.code));
    let candidate = baseCode;
    let suffix = 2;

    while (takenCodes.has(candidate)) {
        candidate = `${baseCode}-${suffix}`;
        suffix += 1;
    }

    return candidate;
};

const TemplateContentEditor: React.FC<TemplateContentEditorProps> = ({
    value,
    onChange,
    smartFields,
    onSmartFieldsChange,
    placeholder = 'Template content...',
    minHeight = '450px',
    showSmartOverlay = false,
}) => {
    const { t } = useTranslation();
    const initialParsedValueRef = useRef<ReturnType<typeof parseTemplateMarkdown> | null>(null);
    if (!initialParsedValueRef.current) {
        initialParsedValueRef.current = parseTemplateMarkdown(value || '');
    }
    const initialParsedValue = initialParsedValueRef.current;
    const [page, setPage] = useState<'template' | 'overlay'>('template');
    const [cleanContent, setCleanContent] = useState(initialParsedValue.cleanContent);
    const [assignments, setAssignments] = useState<FieldAssignment[]>(initialParsedValue.assignments);
    const [selection, setSelection] = useState<{ start: number; end: number } | null>(null);
    const [anchorLine, setAnchorLine] = useState<number | null>(null);
    const [isDraggingSelection, setIsDraggingSelection] = useState(false);
    const [activeResize, setActiveResize] = useState<ActiveResizeState | null>(null);
    const [activeMove, setActiveMove] = useState<ActiveMoveState | null>(null);
    const [lineMetrics, setLineMetrics] = useState<LineMetric[]>([]);
    const [activeResizePreviewY, setActiveResizePreviewY] = useState<number | null>(null);
    const [selectionFieldLabel, setSelectionFieldLabel] = useState('');
    const [isLabelEditorOpen, setIsLabelEditorOpen] = useState(false);
    const [isSelectionMenuOpen, setIsSelectionMenuOpen] = useState(false);
    const committedValueRef = useRef(value || '');
    const canvasRef = useRef<HTMLDivElement | null>(null);
    const lineRefs = useRef<Array<HTMLDivElement | null>>([]);
    const assignmentsRef = useRef<FieldAssignment[]>(initialParsedValue.assignments);
    const anchorLineRef = useRef<number | null>(null);
    const activeResizeRef = useRef<ActiveResizeState | null>(null);
    const activeMoveRef = useRef<ActiveMoveState | null>(null);
    const selectionMenuRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (value === committedValueRef.current) {
            return;
        }

        const parsed = parseTemplateMarkdown(value || '');
        committedValueRef.current = value || '';
        assignmentsRef.current = parsed.assignments;
        anchorLineRef.current = null;
        activeResizeRef.current = null;
        activeMoveRef.current = null;
        setCleanContent(parsed.cleanContent);
        setAssignments(parsed.assignments);
        setSelection(null);
        setAnchorLine(null);
        setIsDraggingSelection(false);
        setActiveResize(null);
        setActiveMove(null);
        setActiveResizePreviewY(null);
    }, [value]);

    useEffect(() => {
        const nextMarkdown = composeTemplateMarkdown(cleanContent, assignments);
        if (nextMarkdown !== value) {
            committedValueRef.current = nextMarkdown;
            onChange(nextMarkdown);
            return;
        }

        committedValueRef.current = value;
    }, [assignments, cleanContent, onChange, value]);

    useEffect(() => {
        assignmentsRef.current = assignments;
    }, [assignments]);

    const lines = useMemo(() => {
        if (!cleanContent.length) {
            return [''];
        }

        return cleanContent.split('\n');
    }, [cleanContent]);

    useEffect(() => {
        lineRefs.current = lineRefs.current.slice(0, lines.length);
    }, [lines.length]);

    useLayoutEffect(() => {
        if (page !== 'overlay') {
            setLineMetrics([]);
            return;
        }

        const nextMetrics = lineRefs.current
            .slice(0, lines.length)
            .map((lineNode) => {
                if (!lineNode) {
                    return null;
                }

                const top = lineNode.offsetTop;
                const height = lineNode.offsetHeight;

                return {
                    top,
                    bottom: top + height,
                    height,
                };
            })
            .filter((metric): metric is LineMetric => metric !== null);

        setLineMetrics(nextMetrics);
    }, [assignments, cleanContent, lines.length, page, selection]);

    useEffect(() => {
        anchorLineRef.current = anchorLine;
    }, [anchorLine]);

    useEffect(() => {
        activeResizeRef.current = activeResize;
    }, [activeResize]);

    useEffect(() => {
        activeMoveRef.current = activeMove;
    }, [activeMove]);

    const assignmentByLine = useMemo(() => {
        const map = new Map<number, FieldAssignment>();
        assignments.forEach((assignment) => {
            for (let lineIndex = assignment.startLine; lineIndex <= assignment.endLine; lineIndex += 1) {
                map.set(lineIndex, assignment);
            }
        });
        return map;
    }, [assignments]);

    const selectedLineIndices = useMemo(() => {
        if (!selection) {
            return [];
        }

        const start = Math.min(selection.start, selection.end);
        const end = Math.max(selection.start, selection.end);
        return Array.from({ length: end - start + 1 }, (_, offset) => start + offset);
    }, [selection]);

    const selectedAssignment = useMemo(() => {
        if (selectedLineIndices.length === 0) {
            return null;
        }

        const first = assignmentByLine.get(selectedLineIndices[0]);
        if (!first) {
            return null;
        }

        const allMatch = selectedLineIndices.every((lineIndex) => {
            const candidate = assignmentByLine.get(lineIndex);
            return candidate && candidate.code === first.code && candidate.startLine === first.startLine && candidate.endLine === first.endLine;
        });

        return allMatch ? first : null;
    }, [assignmentByLine, selectedLineIndices]);

    const selectedField = useMemo(() => {
        if (!selectedAssignment) {
            return null;
        }

        return smartFields.find((field) => field.code === selectedAssignment.code) || null;
    }, [selectedAssignment, smartFields]);

    useEffect(() => {
        if (!selection) {
            setSelectionFieldLabel('');
            return;
        }

        if (!selectedAssignment) {
            setSelectionFieldLabel('');
            return;
        }

        setSelectionFieldLabel(selectedField?.label || selectedAssignment.code);
    }, [selectedAssignment, selectedField, selection]);

    const closeLabelEditor = useCallback(() => {
        setIsLabelEditorOpen(false);
        setSelectionFieldLabel(selectedField?.label || selectedAssignment?.code || '');
    }, [selectedAssignment, selectedField]);

    useEffect(() => {
        if (!isLabelEditorOpen) {
            return undefined;
        }

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                event.preventDefault();
                closeLabelEditor();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [closeLabelEditor, isLabelEditorOpen]);

    useEffect(() => {
        if (!isSelectionMenuOpen) {
            return undefined;
        }

        const handlePointerDown = (event: MouseEvent) => {
            const target = event.target as Node | null;
            if (selectionMenuRef.current && target && !selectionMenuRef.current.contains(target)) {
                setIsSelectionMenuOpen(false);
            }
        };

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setIsSelectionMenuOpen(false);
            }
        };

        window.addEventListener('mousedown', handlePointerDown);
        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('mousedown', handlePointerDown);
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [isSelectionMenuOpen]);

    const handleCleanContentChange = useCallback((nextContent: string) => {
        setCleanContent((previousContent) => {
            if (previousContent !== nextContent) {
                const adjusted = adjustAssignmentsForContentChange(previousContent, nextContent, assignmentsRef.current);
                const normalized = normalizeAssignments(adjusted, nextContent.split('\n').length);
                assignmentsRef.current = normalized;
                setAssignments(normalized);
            }
            return nextContent;
        });
    }, []);

    const resetOverlayInteraction = useCallback(() => {
        anchorLineRef.current = null;
        activeResizeRef.current = null;
        activeMoveRef.current = null;
        setSelection(null);
        setAnchorLine(null);
        setIsDraggingSelection(false);
        setActiveResize(null);
        setActiveMove(null);
        setActiveResizePreviewY(null);
        setIsLabelEditorOpen(false);
        setIsSelectionMenuOpen(false);
    }, []);

    useEffect(() => {
        if (page !== 'overlay' || !selection || isLabelEditorOpen || isSelectionMenuOpen || selectedAssignment) {
            return undefined;
        }

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                event.preventDefault();
                resetOverlayInteraction();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isLabelEditorOpen, isSelectionMenuOpen, page, resetOverlayInteraction, selectedAssignment, selection]);

    const handleLineMouseDown = useCallback((lineIndex: number, useRangeSelection: boolean) => {
        if (activeResizeRef.current) {
            return;
        }

        setIsLabelEditorOpen(false);
        setIsSelectionMenuOpen(false);
        const assignedSelection = assignmentByLine.get(lineIndex);
        if (assignedSelection) {
            anchorLineRef.current = assignedSelection.startLine;
            activeMoveRef.current = {
                code: assignedSelection.code,
                startLine: assignedSelection.startLine,
                endLine: assignedSelection.endLine,
                pointerOffset: lineIndex - assignedSelection.startLine,
            };
            setSelection({
                start: assignedSelection.startLine,
                end: assignedSelection.endLine,
            });
            setAnchorLine(assignedSelection.startLine);
            setActiveMove({
                code: assignedSelection.code,
                startLine: assignedSelection.startLine,
                endLine: assignedSelection.endLine,
                pointerOffset: lineIndex - assignedSelection.startLine,
            });
            setIsDraggingSelection(false);
            return;
        }

        const currentAnchor = anchorLineRef.current;

        if (useRangeSelection && currentAnchor !== null) {
            setSelection({
                start: Math.min(currentAnchor, lineIndex),
                end: Math.max(currentAnchor, lineIndex),
            });
        } else {
            anchorLineRef.current = lineIndex;
            setSelection({ start: lineIndex, end: lineIndex });
            setAnchorLine(lineIndex);
        }

        setIsDraggingSelection(true);
    }, [assignmentByLine]);

    const setLineRef = useCallback((lineIndex: number, node: HTMLDivElement | null) => {
        lineRefs.current[lineIndex] = node;
    }, []);

    const getCanvasPointerY = useCallback((clientY: number) => {
        const canvas = canvasRef.current;
        if (!canvas) {
            return null;
        }

        const canvasRect = canvas.getBoundingClientRect();
        const relativeY = clientY - canvasRect.top + canvas.scrollTop;
        const maxY = Math.max(canvas.scrollHeight, canvas.clientHeight);
        return Math.max(0, Math.min(relativeY, maxY));
    }, []);

    const getLineIndexFromPointer = useCallback((clientX: number, clientY: number) => {
        if (lines.length === 0) {
            return null;
        }

        const canvas = canvasRef.current;
        if (!canvas) {
            return null;
        }

        const canvasRect = canvas.getBoundingClientRect();
        if (clientY <= canvasRect.top) {
            return 0;
        }

        if (clientY >= canvasRect.bottom) {
            return lines.length - 1;
        }

        const hitTarget = document.elementFromPoint(clientX, clientY) as HTMLElement | null;
        const lineElement = hitTarget?.closest?.('[data-line-index]') as HTMLElement | null;
        const rawLineIndex = lineElement?.dataset.lineIndex;

        if (rawLineIndex) {
            const parsedLineIndex = Number(rawLineIndex);
            if (!Number.isNaN(parsedLineIndex)) {
                return parsedLineIndex;
            }
        }

        let closestIndex = 0;
        let closestDistance = Number.POSITIVE_INFINITY;

        lineRefs.current.forEach((lineNode, index) => {
            if (!lineNode) {
                return;
            }

            const lineRect = lineNode.getBoundingClientRect();
            const midpoint = lineRect.top + (lineRect.height / 2);
            const distance = Math.abs(midpoint - clientY);

            if (distance < closestDistance) {
                closestDistance = distance;
                closestIndex = index;
            }
        });

        return Number.isFinite(closestDistance) ? closestIndex : null;
    }, [lines.length]);

    const getResizeLineIndex = useCallback((pointerY: number, edge: 'start' | 'end') => {
        if (lineMetrics.length === 0) {
            return lines.length > 0 ? 0 : null;
        }

        if (edge === 'start') {
            for (let index = lineMetrics.length - 1; index >= 0; index -= 1) {
                if (pointerY >= lineMetrics[index].top) {
                    return index;
                }
            }

            return 0;
        }

        for (let index = 0; index < lineMetrics.length; index += 1) {
            if (pointerY <= lineMetrics[index].bottom) {
                return index;
            }
        }

        return lineMetrics.length - 1;
    }, [lineMetrics, lines.length]);

    const handleResizeMouseDown = useCallback((assignment: FieldAssignment, edge: 'start' | 'end') => {
        const nextResize = {
            code: assignment.code,
            startLine: assignment.startLine,
            endLine: assignment.endLine,
            edge,
        };
        const lineMetric = lineMetrics[edge === 'start' ? assignment.startLine : assignment.endLine];
        const boundaryY = edge === 'start'
            ? lineMetric?.top ?? 0
            : lineMetric?.bottom ?? 0;

        activeResizeRef.current = nextResize;
        anchorLineRef.current = assignment.startLine;
        activeMoveRef.current = null;
        setIsLabelEditorOpen(false);
        setIsSelectionMenuOpen(false);
        setActiveResize(nextResize);
        setActiveMove(null);
        setActiveResizePreviewY(boundaryY);
        setSelection({
            start: assignment.startLine,
            end: assignment.endLine,
        });
        setAnchorLine(assignment.startLine);
        setIsDraggingSelection(false);
    }, [lineMetrics]);

    const applyFieldToSelection = useCallback((code: string) => {
        if (!selection) {
            return;
        }

        const start = Math.min(selection.start, selection.end);
        const end = Math.max(selection.start, selection.end);

        setAssignments((currentAssignments) => {
            const remainingAssignments = currentAssignments.filter((assignment) => assignment.endLine < start || assignment.startLine > end);
            if (!code) {
                return normalizeAssignments(remainingAssignments, lines.length);
            }

            return normalizeAssignments([
                ...remainingAssignments,
                { code, startLine: start, endLine: end },
            ], lines.length);
        });
    }, [lines.length, selection]);

    const removeSelectionField = useCallback(() => {
        if (!selection) {
            return;
        }

        const start = Math.min(selection.start, selection.end);
        const end = Math.max(selection.start, selection.end);
        const removedCode = selectedAssignment?.code || null;
        const nextAssignments = normalizeAssignments(
            assignments.filter((assignment) => assignment.endLine < start || assignment.startLine > end),
            lines.length
        );

        assignmentsRef.current = nextAssignments;
        setAssignments(nextAssignments);
        setSelection({ start, end });
        setSelectionFieldLabel('');
        setIsLabelEditorOpen(false);

        if (removedCode && onSmartFieldsChange && !nextAssignments.some((assignment) => assignment.code === removedCode)) {
            onSmartFieldsChange(smartFields.filter((field) => field.code !== removedCode));
        }
    }, [assignments, lines.length, onSmartFieldsChange, selectedAssignment, selection, smartFields]);

    const saveSelectionField = useCallback(() => {
        if (!selection || !onSmartFieldsChange) {
            return;
        }

        const trimmedLabel = selectionFieldLabel.trim();
        if (!trimmedLabel) {
            return;
        }

        if (selectedAssignment) {
            const nextFields = smartFields.some((field) => field.code === selectedAssignment.code)
                ? smartFields.map((field) => (
                    field.code === selectedAssignment.code
                        ? { ...field, label: trimmedLabel }
                        : field
                ))
                : [
                    ...smartFields,
                    {
                        code: selectedAssignment.code,
                        label: trimmedLabel,
                        description: '',
                        required: false,
                        multiline: true,
                    },
                ];

            onSmartFieldsChange(nextFields);
            setIsLabelEditorOpen(false);
            return;
        }

        const matchingField = smartFields.find((field) => field.label.trim().toLowerCase() === trimmedLabel.toLowerCase());
        const nextCode = matchingField?.code || createUniqueFieldCode(trimmedLabel, smartFields);

        if (!matchingField) {
            onSmartFieldsChange([
                ...smartFields,
                {
                    code: nextCode,
                    label: trimmedLabel,
                    description: '',
                    required: false,
                    multiline: true,
                },
            ]);
        }

        applyFieldToSelection(nextCode);
        setIsLabelEditorOpen(false);
    }, [applyFieldToSelection, onSmartFieldsChange, selectedAssignment, selection, selectionFieldLabel, smartFields]);

    const goToTemplate = useCallback(() => {
        resetOverlayInteraction();
        setPage('template');
    }, [resetOverlayInteraction]);

    const goToOverlay = useCallback(() => {
        resetOverlayInteraction();
        setPage('overlay');
    }, [resetOverlayInteraction]);

    const activeFieldLookup = useMemo(() => {
        const lookup = new Map<string, SmartTemplateField>();
        smartFields.forEach((field) => {
            lookup.set(field.code, field);
        });
        return lookup;
    }, [smartFields]);

    const assignmentSummaries = useMemo(() => assignments.map((assignment) => {
        const field = activeFieldLookup.get(assignment.code);
        return {
            ...assignment,
            title: field?.label || assignment.code,
            preview: getAssignmentPreview(lines, assignment),
            lineLabel: assignment.startLine === assignment.endLine
                ? t('templateContentEditor.lines.single', { line: assignment.startLine + 1 })
                : t('templateContentEditor.lines.range', { start: assignment.startLine + 1, end: assignment.endLine + 1 }),
        };
    }), [activeFieldLookup, assignments, lines, t]);

    const selectionSummary = useMemo(() => {
        if (!selection || selectedAssignment) {
            return null;
        }

        const start = Math.min(selection.start, selection.end);
        const end = Math.max(selection.start, selection.end);

        return {
            startLine: start,
            endLine: end,
            title: t('templateContentEditor.overlay.newSelection'),
            preview: getAssignmentPreview(lines, { code: 'selection', startLine: start, endLine: end }),
        };
    }, [lines, selectedAssignment, selection, t]);

    const focusAssignment = useCallback((assignment: FieldAssignment) => {
        resetOverlayInteraction();
        setSelection({
            start: assignment.startLine,
            end: assignment.endLine,
        });
        setAnchorLine(assignment.startLine);
        anchorLineRef.current = assignment.startLine;

        const lineNode = lineRefs.current[assignment.startLine];
        const canvas = canvasRef.current;

        if (lineNode && canvas) {
            const targetTop = Math.max(lineNode.offsetTop - 48, 0);
            canvas.scrollTo({
                top: targetTop,
                behavior: 'smooth',
            });
        }
    }, [resetOverlayInteraction]);

    const focusSelectionRange = useCallback((startLine: number, endLine: number) => {
        resetOverlayInteraction();
        setSelection({
            start: startLine,
            end: endLine,
        });
        setAnchorLine(startLine);
        anchorLineRef.current = startLine;

        const lineNode = lineRefs.current[startLine];
        const canvas = canvasRef.current;

        if (lineNode && canvas) {
            const targetTop = Math.max(lineNode.offsetTop - 48, 0);
            canvas.scrollTo({
                top: targetTop,
                behavior: 'smooth',
            });
        }
    }, [resetOverlayInteraction]);

    const totalSelections = assignmentSummaries.length + (selectionSummary ? 1 : 0);

    const openLabelEditorForCurrentSelection = useCallback(() => {
        setSelectionFieldLabel(selectedField?.label || selectedAssignment?.code || '');
        setIsLabelEditorOpen(true);
    }, [selectedAssignment, selectedField]);

    const openLabelEditorForAssignment = useCallback((assignment: FieldAssignment) => {
        focusAssignment(assignment);
        const field = smartFields.find((candidate) => candidate.code === assignment.code);
        setSelectionFieldLabel(field?.label || assignment.code);
        setIsLabelEditorOpen(true);
    }, [focusAssignment, smartFields]);

    useEffect(() => {
        if (!isDraggingSelection && !activeResize && !activeMove) {
            return undefined;
        }

        const handleMouseMove = (event: MouseEvent) => {
            const currentResize = activeResizeRef.current;
            if (currentResize) {
                const pointerY = getCanvasPointerY(event.clientY);
                if (pointerY === null) {
                    return;
                }

                setActiveResizePreviewY(pointerY);
                const lineIndex = getResizeLineIndex(pointerY, currentResize.edge);
                if (lineIndex === null) {
                    return;
                }

                const { nextAssignments, nextActiveResize } = resizeAssignmentRange(
                    assignmentsRef.current,
                    currentResize,
                    currentResize.edge,
                    lineIndex,
                    lines.length
                );

                if (!nextActiveResize || isSameResizeState(currentResize, nextActiveResize)) {
                    return;
                }

                assignmentsRef.current = nextAssignments;
                activeResizeRef.current = nextActiveResize;
                anchorLineRef.current = nextActiveResize.startLine;
                setAssignments(nextAssignments);
                setActiveResize(nextActiveResize);
                setSelection({
                    start: nextActiveResize.startLine,
                    end: nextActiveResize.endLine,
                });
                setAnchorLine(nextActiveResize.startLine);
                return;
            }

            const currentMove = activeMoveRef.current;
            if (currentMove) {
                const lineIndex = getLineIndexFromPointer(event.clientX, event.clientY);
                if (lineIndex === null) {
                    return;
                }

                const { nextAssignments, nextActiveMove } = moveAssignmentRange(
                    assignmentsRef.current,
                    currentMove,
                    lineIndex,
                    currentMove.pointerOffset,
                    lines.length
                );

                if (!nextActiveMove || isSameMoveState(currentMove, nextActiveMove)) {
                    return;
                }

                assignmentsRef.current = nextAssignments;
                activeMoveRef.current = nextActiveMove;
                anchorLineRef.current = nextActiveMove.startLine;
                setAssignments(nextAssignments);
                setActiveMove(nextActiveMove);
                setSelection({
                    start: nextActiveMove.startLine,
                    end: nextActiveMove.endLine,
                });
                setAnchorLine(nextActiveMove.startLine);
                return;
            }

            const lineIndex = getLineIndexFromPointer(event.clientX, event.clientY);
            if (lineIndex === null) {
                return;
            }

            const currentAnchor = anchorLineRef.current;
            if (!isDraggingSelection || currentAnchor === null) {
                return;
            }

            const nextSelection = {
                start: Math.min(currentAnchor, lineIndex),
                end: Math.max(currentAnchor, lineIndex),
            };

            setSelection((currentSelection) => {
                if (currentSelection?.start === nextSelection.start && currentSelection.end === nextSelection.end) {
                    return currentSelection;
                }

                return nextSelection;
            });
        };

        const stopDragging = () => {
            activeResizeRef.current = null;
            activeMoveRef.current = null;
            setIsDraggingSelection(false);
            setActiveResize(null);
            setActiveMove(null);
            setActiveResizePreviewY(null);
        };

        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', stopDragging);
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', stopDragging);
        };
    }, [activeMove, activeResize, getCanvasPointerY, getLineIndexFromPointer, getResizeLineIndex, isDraggingSelection, lines.length]);

    return (
        <div className="space-y-4">
            {showSmartOverlay && (
                <div className="inline-flex rounded-xl border border-gray-200 bg-white p-1 shadow-sm">
                    <button
                        type="button"
                        onClick={goToTemplate}
                        className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${page === 'template'
                            ? 'bg-primary-600 text-white'
                            : 'text-gray-600 hover:bg-gray-50'
                            }`}
                    >
                        {t('templateContentEditor.tabs.template')}
                    </button>
                    <button
                        type="button"
                        onClick={goToOverlay}
                        className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${page === 'overlay'
                            ? 'bg-primary-600 text-white'
                            : 'text-gray-600 hover:bg-gray-50'
                            }`}
                    >
                        {t('templateContentEditor.tabs.smartOverlay')}
                    </button>
                </div>
            )}

            {(!showSmartOverlay || page === 'template') ? (
                <MarkdownEditor
                    value={cleanContent}
                    onChange={handleCleanContentChange}
                    placeholder={placeholder}
                    minHeight={minHeight}
                />
            ) : (
                <div className="rounded-xl border border-gray-200 bg-white overflow-hidden shadow-sm">
                                <div className="relative border-b border-gray-100 bg-gradient-to-b from-gray-50 to-white px-3 py-2">
                                    <div className="flex min-h-[40px] items-center justify-between gap-3">
                                        <div className="text-sm font-medium text-gray-700">
                                            {t('templateContentEditor.overlay.title')}
                                        </div>

                                        <div className="flex items-center gap-2">
                                            {selectedLineIndices.length > 0 && (
                                                <button
                                                    type="button"
                                                    onClick={resetOverlayInteraction}
                                                    className="inline-flex items-center rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-600 hover:bg-gray-50"
                                                >
                                                    {t('templateContentEditor.overlay.clearSelection')}
                                                </button>
                                            )}

                                            <div ref={selectionMenuRef} className="relative">
                                                <button
                                                    type="button"
                                                    onClick={() => setIsSelectionMenuOpen((current) => !current)}
                                                    className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50"
                                                >
                                                    <span>{t('templateContentEditor.overlay.selectionsCount', { count: totalSelections })}</span>
                                                    <svg className={`h-4 w-4 text-gray-400 transition-transform ${isSelectionMenuOpen ? 'rotate-180' : ''}`} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                                        <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.51a.75.75 0 01-1.08 0l-4.25-4.51a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                                                    </svg>
                                                </button>

                                                {isSelectionMenuOpen && (
                                                    <div className="absolute right-0 top-[calc(100%+8px)] z-40 w-[320px] overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl">
                                                        {totalSelections > 0 ? (
                                                            <div className="max-h-[320px] overflow-auto py-2">
                                                                {assignmentSummaries.map((assignment) => {
                                                                    const isActive = Boolean(
                                                                        selection &&
                                                                        Math.min(selection.start, selection.end) === assignment.startLine &&
                                                                        Math.max(selection.start, selection.end) === assignment.endLine
                                                                    );

                                                                    return (
                                                                        <button
                                                                            key={`${assignment.code}-${assignment.startLine}-${assignment.endLine}`}
                                                                            type="button"
                                                                            onClick={() => {
                                                                                setIsSelectionMenuOpen(false);
                                                                                focusAssignment(assignment);
                                                                            }}
                                                                            className={`flex w-full items-start gap-3 px-4 py-3 text-left transition-colors ${isActive ? 'bg-primary-50' : 'hover:bg-gray-50'}`}
                                                                        >
                                                                            <span className={`mt-1 h-2.5 w-2.5 shrink-0 rounded-full border ${getFieldTint(assignment.code).base}`}></span>
                                                                            <span className="min-w-0 flex-1">
                                                                                <span className="block truncate text-sm font-medium text-gray-900">{assignment.title}</span>
                                                                                <span className="mt-0.5 block truncate text-xs text-gray-500">{assignment.preview}</span>
                                                                            </span>
                                                                            <span className="shrink-0 text-[11px] font-medium uppercase tracking-wide text-gray-400">
                                                                                {assignment.lineLabel}
                                                                            </span>
                                                                        </button>
                                                                    );
                                                                })}

                                                                {selectionSummary && (
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => {
                                                                            setIsSelectionMenuOpen(false);
                                                                            focusSelectionRange(selectionSummary.startLine, selectionSummary.endLine);
                                                                        }}
                                                                        className="flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-gray-50"
                                                                    >
                                                                        <span className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full border border-primary-300 bg-white"></span>
                                                                        <span className="min-w-0 flex-1">
                                                                            <span className="block truncate text-sm font-medium text-gray-900">{selectionSummary.title}</span>
                                                                            <span className="mt-0.5 block truncate text-xs text-gray-500">{selectionSummary.preview}</span>
                                                                        </span>
                                                                        <span className="shrink-0 text-[11px] font-medium uppercase tracking-wide text-gray-400">
                                                                            {t('templateContentEditor.lines.range', { start: selectionSummary.startLine + 1, end: selectionSummary.endLine + 1 })}
                                                                        </span>
                                                                    </button>
                                                                )}
                                                            </div>
                                                        ) : (
                                                            <div className="px-4 py-4 text-sm text-gray-500">
                                                                {t('templateContentEditor.overlay.noSelections')}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div
                                    ref={canvasRef}
                                    className="relative overflow-auto bg-white px-5 py-4 select-none"
                                    style={{ minHeight }}
                                >
                                    {lines.map((line, index) => {
                                        const lineAssignment = assignmentByLine.get(index);
                                        const isSelected = selectedLineIndices.includes(index);
                                        const lineDescriptor = getVisualLineDescriptor(line);
                                        // Blanco regels rondom koppen of tussen lijstitems zijn puur
                                        // structurele markdown-opmaak; klap ze in zodat de overlay
                                        // overeenkomt met de gerenderde weergave.
                                        const isStructuralBlank = isStructuralBlankLine(lines, index);
                                        const field = lineAssignment ? activeFieldLookup.get(lineAssignment.code) : undefined;
                                        const selectionStart = selection ? Math.min(selection.start, selection.end) : -1;
                                        const isSelectionStart = isSelected && index === selectionStart;
                                        const previousSelection = selectedLineIndices.includes(index - 1);
                                        const nextSelection = selectedLineIndices.includes(index + 1);
                                        const sameAssignedPrevious = Boolean(
                                            lineAssignment &&
                                            assignmentByLine.get(index - 1) &&
                                            assignmentByLine.get(index - 1)?.code === lineAssignment.code &&
                                            assignmentByLine.get(index - 1)?.startLine === lineAssignment.startLine &&
                                            assignmentByLine.get(index - 1)?.endLine === lineAssignment.endLine
                                        );
                                        const sameAssignedNext = Boolean(
                                            lineAssignment &&
                                            assignmentByLine.get(index + 1) &&
                                            assignmentByLine.get(index + 1)?.code === lineAssignment.code &&
                                            assignmentByLine.get(index + 1)?.startLine === lineAssignment.startLine &&
                                            assignmentByLine.get(index + 1)?.endLine === lineAssignment.endLine
                                        );
                                        const sectionTint = lineAssignment ? getFieldTint(lineAssignment.code) : null;
                                        const tint = isSelected
                                            ? lineAssignment
                                                ? `${sectionTint?.active || ''} text-gray-900`
                                                : 'border-primary-300 bg-primary-50/70 text-gray-900'
                                            : lineAssignment
                                                ? sectionTint?.base || ''
                                                : 'border-transparent bg-transparent text-gray-700';
                                        const showAssignedLabel = Boolean(lineAssignment && lineAssignment.startLine === index);
                                        const showSelectionHeader = Boolean(isSelectionStart);
                                        const showTopResizeHandle = Boolean(lineAssignment && lineAssignment.startLine === index);
                                        const showBottomResizeHandle = Boolean(lineAssignment && lineAssignment.endLine === index);
                                        const isTopResizeActive = Boolean(
                                            activeResize &&
                                            lineAssignment &&
                                            isSameAssignment(activeResize, lineAssignment) &&
                                            activeResize.edge === 'start'
                                        );
                                        const isBottomResizeActive = Boolean(
                                            activeResize &&
                                            lineAssignment &&
                                            isSameAssignment(activeResize, lineAssignment) &&
                                            activeResize.edge === 'end'
                                        );
                                        const connectPrevious = isSelected ? previousSelection : sameAssignedPrevious;
                                        const connectNext = isSelected ? nextSelection : sameAssignedNext;
                                        const isMultiLineSelection = isSelected && (previousSelection || nextSelection);
                                        const lineChrome = isSelected || lineAssignment;
                                        const headingLevel = lineDescriptor.kind === 'heading' ? lineDescriptor.level : 0;
                                        const listIndent = lineDescriptor.kind === 'unordered-list' || lineDescriptor.kind === 'ordered-list'
                                            ? lineDescriptor.indent
                                            : 0;
                                        const orderedListMarker = lineDescriptor.kind === 'ordered-list' ? lineDescriptor.marker : '';
                                        const contentClassName = lineDescriptor.kind === 'heading'
                                            ? headingLevel === 1
                                                ? 'px-[2px] py-[3px] text-[24px] font-bold leading-[1.2] text-[rgb(55,53,47)]'
                                                : headingLevel === 2
                                                    ? 'px-[2px] py-[3px] text-[20px] font-semibold leading-[1.3] text-[rgb(55,53,47)]'
                                                    : headingLevel === 3
                                                        ? 'px-[2px] py-[3px] text-[18px] font-semibold leading-[1.3] text-[rgb(55,53,47)]'
                                                        : 'px-[2px] py-[3px] text-[17px] font-semibold leading-[1.4] text-[rgb(55,53,47)]'
                                            : lineDescriptor.kind === 'quote'
                                                ? 'px-4 py-[3px] text-[16px] leading-[1.5] text-[rgba(55,53,47,0.6)]'
                                                : lineDescriptor.kind === 'blank'
                                                    ? isStructuralBlank
                                                        ? 'h-0 overflow-hidden px-0 py-0'
                                                        : 'min-h-[6px] px-0 py-0 text-[rgb(55,53,47)]'
                                                    : 'px-[2px] py-[3px] text-[16px] leading-[1.5] text-[rgb(55,53,47)]';

                                        return (
                                            <div
                                                key={`line-${index}`}
                                                ref={(node) => setLineRef(index, node)}
                                                data-line-index={index}
                                                onMouseDown={(event) => {
                                                    if (event.button !== 0) {
                                                        return;
                                                    }
                                                    event.preventDefault();
                                                    handleLineMouseDown(index, event.shiftKey);
                                                }}
                                                className={`group relative w-full text-left ${showTopResizeHandle || showBottomResizeHandle ? 'z-[1]' : ''}`}
                                            >
                                                <div
                                                    className={`pointer-events-none absolute inset-0 transition-colors ${lineChrome
                                                        ? `${tint} border-x-2`
                                                        : 'group-hover:bg-gray-50/70'
                                                        } ${isSelected && !isMultiLineSelection ? `ring-2 ring-inset ${lineAssignment ? sectionTint?.activeRing || '' : 'ring-primary-200/80'}` : ''} ${lineChrome && !connectPrevious ? 'rounded-t-[14px] border-t-2' : ''} ${lineChrome && !connectNext ? 'rounded-b-[14px] border-b-2' : ''} ${lineChrome && connectPrevious ? 'border-t-0' : ''} ${lineChrome && connectNext ? 'border-b-0' : ''} ${isSelected && previousSelection ? 'border-t-0' : ''} ${isSelected && nextSelection ? 'border-b-0' : ''}`}
                                                ></div>

                                                {isSelected && isMultiLineSelection && (
                                                    <div
                                                        className={`pointer-events-none absolute inset-0 ${!connectPrevious ? 'rounded-t-[14px]' : ''} ${!connectNext ? 'rounded-b-[14px]' : ''}`}
                                                        style={{
                                                            boxShadow: [
                                                                !connectPrevious ? `inset 0 2px 0 0 ${lineAssignment ? sectionTint?.activeEdge || 'rgba(9, 66, 58, 0.35)' : 'rgba(9, 66, 58, 0.35)'}` : '',
                                                                !connectNext ? `inset 0 -2px 0 0 ${lineAssignment ? sectionTint?.activeEdge || 'rgba(9, 66, 58, 0.35)' : 'rgba(9, 66, 58, 0.35)'}` : '',
                                                            ].filter(Boolean).join(', '),
                                                        }}
                                                    ></div>
                                                )}

                                                {showTopResizeHandle && lineAssignment && !isTopResizeActive && (
                                                    <button
                                                        type="button"
                                                        onMouseDown={(event) => {
                                                            event.preventDefault();
                                                            event.stopPropagation();
                                                            handleResizeMouseDown(lineAssignment, 'start');
                                                        }}
                                                        className="absolute -top-[10px] left-5 right-5 z-20 flex h-5 cursor-row-resize items-center justify-center"
                                                        title={t('templateContentEditor.overlay.resizeTop')}
                                                    >
                                                        <span className={`h-1.5 w-full rounded-full border shadow-sm ${sectionTint?.base || ''}`}></span>
                                                    </button>
                                                )}

                                                {(showAssignedLabel || showSelectionHeader) && (
                                                    <div className={`absolute right-5 z-30 flex flex-col items-end gap-2 ${showAssignedLabel || showSelectionHeader ? '-top-3' : ''}`}>
                                                        {showSelectionHeader ? (
                                                            <button
                                                                type="button"
                                                                onMouseDown={(event) => event.stopPropagation()}
                                                                onClick={(event) => {
                                                                    event.stopPropagation();
                                                                    openLabelEditorForCurrentSelection();
                                                                }}
                                                                className={`pointer-events-auto inline-flex items-center rounded-full border bg-white/95 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-700 shadow-sm backdrop-blur ${selectedAssignment ? sectionTint?.active || '' : 'border-primary-200 bg-primary-50/95 text-primary-800'}`}
                                                            >
                                                                {selectedAssignment
                                                                    ? selectedField?.label || selectedAssignment.code
                                                                    : t('templateContentEditor.overlay.newField')}
                                                            </button>
                                                        ) : showAssignedLabel && field ? (
                                                            <button
                                                                type="button"
                                                                onMouseDown={(event) => event.stopPropagation()}
                                                                onClick={(event) => {
                                                                    event.stopPropagation();
                                                                    if (lineAssignment) {
                                                                        openLabelEditorForAssignment(lineAssignment);
                                                                    }
                                                                }}
                                                                className={`pointer-events-auto inline-flex items-center rounded-full border bg-white/95 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-700 shadow-sm backdrop-blur ${sectionTint?.base || ''}`}
                                                            >
                                                                {field.label || field.code}
                                                            </button>
                                                        ) : null}

                                                        {isSelectionStart && isLabelEditorOpen && (
                                                            <div
                                                                onMouseDown={(event) => event.stopPropagation()}
                                                                className="pointer-events-auto flex max-w-[360px] items-center gap-2 rounded-2xl border border-primary-200 bg-white/96 px-3 py-2 shadow-lg backdrop-blur"
                                                            >
                                                                <input
                                                                    type="text"
                                                                    value={selectionFieldLabel}
                                                                    placeholder={t('templateContentEditor.overlay.fieldNamePlaceholder')}
                                                                    autoFocus
                                                                    onMouseDown={(event) => event.stopPropagation()}
                                                                    onClick={(event) => event.stopPropagation()}
                                                                    onChange={(event) => setSelectionFieldLabel(event.target.value)}
                                                                    onKeyDown={(event) => {
                                                                        event.stopPropagation();
                                                                        if (event.key === 'Enter') {
                                                                            event.preventDefault();
                                                                            saveSelectionField();
                                                                        }
                                                                        if (event.key === 'Escape') {
                                                                            event.preventDefault();
                                                                            closeLabelEditor();
                                                                        }
                                                                    }}
                                                                    className="min-w-[220px] rounded-lg border border-primary-200 bg-white px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                                                />
                                                                <button
                                                                    type="button"
                                                                    onMouseDown={(event) => event.stopPropagation()}
                                                                    onClick={(event) => {
                                                                        event.stopPropagation();
                                                                        saveSelectionField();
                                                                    }}
                                                                    className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700 transition-colors hover:bg-emerald-100"
                                                                    title={t('common.save')}
                                                                    aria-label={t('common.save')}
                                                                >
                                                                    <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                                                        <path fillRule="evenodd" d="M16.704 5.29a1 1 0 010 1.42l-7.2 7.2a1 1 0 01-1.415 0l-3-3a1 1 0 111.414-1.42l2.293 2.294 6.493-6.494a1 1 0 011.415 0z" clipRule="evenodd" />
                                                                    </svg>
                                                                </button>
                                                                {selectedAssignment && (
                                                                    <button
                                                                        type="button"
                                                                        onMouseDown={(event) => event.stopPropagation()}
                                                                        onClick={(event) => {
                                                                            event.stopPropagation();
                                                                            removeSelectionField();
                                                                        }}
                                                                        className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-red-200 bg-red-50 text-red-600 transition-colors hover:bg-red-100"
                                                                        title={t('common.delete')}
                                                                        aria-label={t('common.delete')}
                                                                    >
                                                                        <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                                                            <path fillRule="evenodd" d="M8.5 3a1 1 0 00-.894.553L7.382 4H5a1 1 0 100 2h.293l.864 9.063A2 2 0 008.148 17h3.704a2 2 0 001.991-1.937L14.707 6H15a1 1 0 100-2h-2.382l-.224-.447A1 1 0 0011.5 3h-3zm1.5 4a1 1 0 00-1 1v5a1 1 0 102 0V8a1 1 0 00-1-1zm-3 1a1 1 0 012 0v5a1 1 0 11-2 0V8zm6-1a1 1 0 00-1 1v5a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                                                        </svg>
                                                                    </button>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}

                                                <div
                                                    className={`relative z-10 min-w-0 ${contentClassName} ${lineDescriptor.kind === 'quote' ? 'border-l-[3px] border-[rgba(55,53,47,0.18)] bg-[rgba(241,241,239,0.6)] rounded-r-[3px]' : ''}`}
                                                    style={lineDescriptor.kind === 'unordered-list' || lineDescriptor.kind === 'ordered-list'
                                                        ? { paddingLeft: `${24 + (listIndent * 24)}px` }
                                                        : undefined}
                                                >
                                                    {lineDescriptor.kind === 'unordered-list' && (
                                                        <span className="mr-2 inline-block w-4 text-center text-[16px] leading-[1.5] text-[rgb(55,53,47)]">
                                                            •
                                                        </span>
                                                    )}
                                                    {lineDescriptor.kind === 'ordered-list' && (
                                                        <span className="mr-2 inline-block min-w-[1.5rem] text-right text-[16px] leading-[1.5] text-[rgb(55,53,47)]">
                                                            {orderedListMarker}
                                                        </span>
                                                    )}
                                                    {lineDescriptor.kind === 'blank' ? (
                                                        <span className="text-transparent">spacer</span>
                                                    ) : (
                                                        <span key={lineDescriptor.html} dangerouslySetInnerHTML={{ __html: lineDescriptor.html }} />
                                                    )}
                                                </div>

                                                {showBottomResizeHandle && lineAssignment && !isBottomResizeActive && (
                                                    <button
                                                        type="button"
                                                        onMouseDown={(event) => {
                                                            event.preventDefault();
                                                            event.stopPropagation();
                                                            handleResizeMouseDown(lineAssignment, 'end');
                                                        }}
                                                        className="absolute -bottom-[10px] left-5 right-5 z-20 flex h-5 cursor-row-resize items-center justify-center"
                                                        title={t('templateContentEditor.overlay.resizeBottom')}
                                                    >
                                                        <span className={`h-1.5 w-full rounded-full border shadow-sm ${sectionTint?.base || ''}`}></span>
                                                    </button>
                                                )}
                                            </div>
                                        );
                                    })}

                                    {activeResize && activeResizePreviewY !== null && (
                                        <div
                                            className="pointer-events-none absolute left-5 right-5 z-30"
                                            style={{ top: `${Math.max(activeResizePreviewY - 6, 0)}px` }}
                                        >
                                            <div className={`h-3 rounded-full border shadow-lg ${getFieldTint(activeResize.code).base} ring-2 ${getFieldTint(activeResize.code).activePreviewRing}`}></div>
                                        </div>
                                    )}
                                </div>
                </div>
            )}
        </div>
    );
};

export default TemplateContentEditor;
